import { Injectable } from '@angular/core';
import { AuthService as Auth0Service, User } from '@auth0/auth0-angular';
import { datadogRum } from '@datadog/browser-rum';
import { Observable } from 'rxjs';
import { catchError, concatMap } from 'rxjs/operators';
import { AppPermissions } from 'src/app/consts/app-permissions';
import { AuthInfo } from 'src/app/interfaces/auth/auth-info';
import { ITokenResult } from 'src/app/interfaces/auth/token-result';
import { ProfileService } from 'src/app/services/profile.service';

const localStorageKeys = {
  canCreateInvoices: 'invoice',
  canCancelInventory: 'cancel',
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private authInfo: AuthInfo | null = null;

  private isTokenLocked = false;
  private isTokenCanBeLocked = true;

  constructor(
    public auth0: Auth0Service,
    public profileService: ProfileService
  ) {
    this.auth0.isAuthenticated$.subscribe((isAuthenticated) => {
      if (!isAuthenticated) {
        return;
      }
      this.auth0.getAccessTokenSilently().subscribe(
        (token) => {
          this.authInfo = this.decodeJwt(token);

          this.auth0.user$.subscribe((user) => {
            if (!user) {
              return;
            }

            datadogRum.setUser({
              id: user.sub,
              name: user.name,
              email: user.email,
            });

            if (!this.authInfo) {
              return;
            }

            this.authInfo.id = user.sub;
            if (user.family_name?.length || user.given_name?.length) {
              this.authInfo.name = `${user.given_name} ${user.family_name}`;
            } else {
              this.authInfo.name = user.name ?? user.email;
            }
            this.authInfo.email = user.email;
          });
        },
        (error) => {
          console.log('Error getting token', error);
        }
      );
    });
  }

  public get accessToken(): string | null {
    return this.authInfo?.accessToken;
  }

  public get isAuthorized(): Observable<boolean> {
    return this.auth0.isAuthenticated$;
  }

  public get fullName(): string {
    return this.authInfo?.name;
  }

  public get role(): string {
    return this.authInfo?.role;
  }

  public get isInitialized$(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      if (this.authInfo) {
        observer.next(true);
        observer.complete();
      } else {
        const interval = setInterval(() => {
          if (this.authInfo) {
            observer.next(true);
            observer.complete();
            clearInterval(interval);
          }
        }, 400);
      }
    });
  }

  public get isAdmin(): boolean {
    return this.hasPermission('admin-access');
  }

  public get isManager(): boolean {
    return this.hasPermission('manager-access');
  }

  public get isElevatedPrivileges(): boolean {
    return this.isAdmin || this.isManager;
  }

  public get canChangePrice(): boolean {
    return (
      this.isAdmin ||
      this.isManager ||
      this.hasPermission(AppPermissions.inventoryEditPrice)
    );
  }

  public get canEditBroadcast(): boolean {
    return (
      this.isAdmin ||
      this.isManager ||
      this.hasPermission(AppPermissions.inventoryEditBroadcast)
    );
  }

  public get canEditInventoryTags(): boolean {
    return (
      this.isAdmin ||
      this.isManager ||
      this.hasPermission(AppPermissions.inventoryEditTags)
    );
  }

  public get canMassEdit(): boolean {
    return (
      this.isAdmin ||
      this.isManager ||
      this.hasPermission(AppPermissions.inventoryMassEdit)
    );
  }

  public get canEditInventory(): boolean {
    return (
      this.isAdmin ||
      this.isManager ||
      this.hasPermission(AppPermissions.inventoryEdit)
    );
  }

  public get canAddInventory(): boolean {
    return (
      this.isAdmin ||
      this.isManager ||
      this.hasPermission(AppPermissions.inventoryAdd)
    );
  }

  public get canHoldInventory(): boolean {
    return (
      this.isAdmin ||
      this.isManager ||
      this.hasPermission(AppPermissions.inventoryHold)
    );
  }

  public get canMergeInventory(): boolean {
    return (
      this.isAdmin ||
      this.isManager ||
      this.hasPermission(AppPermissions.inventoryMerge)
    );
  }

  public get canSplitInventory(): boolean {
    return (
      this.isAdmin ||
      this.isManager ||
      this.hasPermission(AppPermissions.inventorySplit)
    );
  }

  public get canAccessPayments(): boolean {
    return (
      this.isAdmin ||
      this.isManager ||
      this.hasPermission(AppPermissions.paymentsAccess)
    );
  }

  public get canAccessPoAccounts(): boolean {
    return (
      this.isAdmin ||
      this.isManager ||
      this.hasPermission(AppPermissions.poAccountsAccess)
    );
  }

  public get canAccessAccounts(): boolean {
    return (
      this.isAdmin ||
      (this.isManager && this.hasPermission(AppPermissions.accountsAccess))
    );
  }

  public get canPerformAccountsActions(): boolean {
    return (
      this.isAdmin ||
      (this.isManager && this.hasPermission(AppPermissions.accountsActions))
    );
  }

  public get canCreateInvoices(): boolean {
    return (
      window.localStorage.getItem(localStorageKeys.canCreateInvoices) ===
        'true' && window.location.hostname === 'localhost'
    );
  }

  public get canCancelInventory(): boolean {
    return (
      window.localStorage.getItem(localStorageKeys.canCancelInventory) ===
        'true' && window.location.hostname === 'localhost'
    );
  }

  public lockToken(): boolean {
    if (!this.isTokenLocked && this.isTokenCanBeLocked) {
      this.isTokenLocked = true;
      this.isTokenCanBeLocked = false;
      setTimeout(() => (this.isTokenCanBeLocked = true), 1000 * 60 * 30);
      return true;
    }
    return false;
  }

  public logout(): Observable<void> {
    const isLocalhost = window.location.hostname === 'localhost';
    const logoutParams = isLocalhost
      ? { returnTo: 'http://localhost:4200' }
      : undefined;
    if (!this.profileService.invalidateToken) {
      return this.auth0.logout({ logoutParams });
    }

    return this.profileService.invalidateToken().pipe(
      concatMap(() => {
        return this.auth0.logout({ logoutParams });
      }),
      catchError(() => {
        return this.auth0.logout({ logoutParams });
      })
    );
  }

  public hasPermission(permission: string): boolean {
    return (this.authInfo?.permissions ?? []).indexOf(permission) !== -1;
  }

  // TODO: remove
  public hasRole(role: string): boolean {
    return this.authInfo?.role === role;
  }

  public refresh(): void {}

  public async refreshAsync(): Promise<ITokenResult> {
    const result = await this.auth0.getAccessTokenSilently().toPromise();
    return {
      accessToken: result,
      refreshToken: null,
    };
  }

  public resetToken() {}
  // END TODO: remove

  private decodeJwt(jwt: string): AuthInfo {
    const base64Url = jwt.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const json = window.atob(base64);
    const payload = JSON.parse(json);

    return new AuthInfo(
      jwt,
      payload.id,
      payload.name,
      payload.email,
      payload.role,
      payload.permissions ?? [],
      payload.exp
    );
  }
}
